/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PrimaryLargeButton from "../../../components/_utils/CommonButtons/PrimaryLargeButton";
import PasswordInputField from "../../../components/_utils/CommonInputFields/PasswordInputField";
import {
  changePassword,
  resetChangePassword,
  resetFirstLoginData,
  setMultipleEntity,
  setShowReloginModalFalse,
} from "../../../redux/features/login/login";
import { logout } from "../../../redux/features/refreshToken/refreshToken";
import ICONS from "../../../assets/Icons";
import CommonToast from "../../../components/_utils/CommonToast";
import CommonAlertCard from "../../../components/_utils/CommonAlertCard";
import { toast } from "react-toastify";

const SetNewPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const oldInputRef = useRef(null);
  const newInputRef = useRef(null);
  const confirmInputRef = useRef(null);
  const [passwordError, setPasswordError] = useState(false);
  const [isLabelLoading, setIsLabelLoading] = useState(false);
  const [password, setPassword] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const { changePasswordError } = useSelector((state) => state.login);
  const handleChange = (field, value) => {
    setPassword((prev) => ({
      ...prev,
      [field]: value.replace(/\s/g, ""),
    }));
    if (passwordError) setPasswordError(false);
    dispatch(resetChangePassword());
  };

  const handleSetNewPassword = useCallback(async () => {
    if (
      password?.oldPassword === "" ||
      password?.newPassword === "" ||
      password?.confirmPassword === "" ||
      changePasswordError ||
      passwordError
    ) {
      return;
    } else if (
      password?.oldPassword?.length > 0 &&
      password?.newPassword !== password?.confirmPassword
    ) {
      setPasswordError(true);
      toast.dismiss();
      CommonToast({
        text: "Failed to change password.",
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-bg)",
        },
      });
    } else if (
      password?.oldPassword?.length > 0 &&
      password?.newPassword?.length > 0 &&
      password?.confirmPassword?.length > 0
    ) {
      setIsLabelLoading(true);

      // Loading toast
      toast.dismiss();
      CommonToast({
        text: "Resetting password.",
        icon: "https://q2pay.in/new_assets/gifs/inputloader_white.gif",
        variant: "var(--processing)",
        buttonNeeded: false,
        btnProps: {
          enabledBgColor: "var(--primary-bg)",
          enabledTextColor: "var(--text-primary)",
          btnText: "",
          handleFunction: () => {},
          icon: "",
          btnType: "button",
          isDisabled: false,
          hoverOption: "white",
        },
      });

      const params = {
        oldPassword: password?.oldPassword,
        newPassword: password?.newPassword,
        confirmPassword: password?.confirmPassword,
        setButtonLoading: setIsLabelLoading,
      };

      const response = await dispatch(changePassword(params));

      if (response?.payload?.status) {
        toast.dismiss();
        CommonToast({
          text: "Password reset successfully.",
          icon: ICONS?.SELECTED_WITH_WHITE_FILLED,
          variant: "var(--success)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });

        dispatch(setMultipleEntity());
        dispatch(setShowReloginModalFalse());
        dispatch(logout());
        dispatch(resetFirstLoginData());

        navigate("/login", { replace: true });
      } else {
        setIsLabelLoading(false);
      }
    }
  }, [password, changePasswordError, passwordError]);

  const handleEnterKey = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef && nextRef.current) {
        nextRef.current.focus();
      } else {
        if (
          password?.oldPassword === "" ||
          password?.newPassword === "" ||
          password?.confirmPassword === "" ||
          changePasswordError ||
          passwordError
        ) {
          return;
        } else {
          handleSetNewPassword();
        }
      }
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleEnterKey);
    return () => {
      document.removeEventListener("keydown", handleEnterKey);
    };
  }, [password, changePasswordError, passwordError]);

  return (
    <div
      className="h-screen flex justify-center"
      style={{
        backgroundImage:
          "url('https://q2pay.in/new_assets/graphics/login/backgrond_for_login.svg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {" "}
      <div className="w-full h-full grid place-items-center  Qlg:p-padding-base p-padding-sm">
        <div className="w-full Qsmd:w-fit rounded-border-radius-md p-[var(--border)] bg-[linear-gradient(154.44deg,rgba(97,117,153,0.5)_16.18%,rgba(100,100,100,0.5)_100.4%)] shadow-[0px_4px_4px_0px_#00000033]">
          <div className="Qsmd:p-padding-xl px-padding-sm py-padding-lg rounded-border-radius-md bg-primary-bg Qsmd:w-[var(--login-card-height)] w-full  h-fit">
            <div className="flex flex-col gap-gap-xl items-center">
              <div className="w-xpentra-logo-width-login h-[var(--gap-lg)]">
                <img
                  src={ICONS?.XPENTRA_WHITE}
                  alt=""
                />
              </div>

              <div className="flex flex-col  items-start justify-between w-full ">
                <CommonAlertCard
                  texts={
                    "After changing the password, you’ll be logged out and need to log in again."
                  }
                />
                <h4 className="text-font-sm font-semibold font-sans text-text-primary mb-margin-xs mt-margin-lg ">
                  Set new password
                </h4>
                <p className=" text-font-xs font-normal font-sans text-text-primary mb-margin-lg ">
                  Enter current password, then set and confirm your new
                  password.
                </p>

                <div className="w-full flex flex-col gap-gap-md">
                  <PasswordInputField
                    ref={oldInputRef}
                    placeholder="Current password"
                    bgColor="var(--primary-bg)"
                    fgColor="var(--text-secondary)"
                    isFullWidth={true}
                    value={password.oldPassword}
                    onInput={(e) => handleChange("oldPassword", e.target.value)}
                    variant={
                      changePasswordError
                        ? "error"
                        : password.oldPassword
                          ? "typing"
                          : "default"
                    }
                    onKeyDown={(e) => handleEnterKey(e, newInputRef)}
                    className="w-width-full"
                  />

                  <PasswordInputField
                    ref={newInputRef}
                    placeholder="New password"
                    value={password.newPassword}
                    onInput={(e) => handleChange("newPassword", e.target.value)}
                    variant={
                      passwordError
                        ? "error"
                        : password.newPassword
                          ? "typing"
                          : "default"
                    }
                    onKeyDown={(e) => handleEnterKey(e, confirmInputRef)}
                    className="w-width-full"
                    bgColor="var(--primary-bg)"
                    fgColor="var(--text-secondary)"
                    isFullWidth={true}
                  />

                  <div className="w-full">
                    <PasswordInputField
                      ref={confirmInputRef}
                      placeholder="Confirm new password"
                      value={password.confirmPassword}
                      onInput={(e) =>
                        handleChange("confirmPassword", e.target.value)
                      }
                      variant={
                        passwordError
                          ? "error"
                          : password.confirmPassword
                            ? "typing"
                            : "default"
                      }
                      onKeyDown={(e) => handleEnterKey(e, null)}
                      className="w-width-full"
                      bgColor="var(--primary-bg)"
                      fgColor="var(--text-secondary)"
                      isFullWidth={true}
                    />
                    {passwordError && (
                      <p className="text-failed text-xs font-semibold mt-margin-xs ml-margin-xs">
                        Passwords do not match.
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <PrimaryLargeButton
                btnText="Change password"
                btnType="button"
                handleFunction={handleSetNewPassword}
                isDisabled={
                  changePasswordError ||
                  passwordError ||
                  !(
                    password?.oldPassword?.length > 0 &&
                    password?.newPassword?.length > 0 &&
                    password?.confirmPassword?.length > 0
                  )
                }
                enabledTextColor="var(--text-primary)"
                disabledTextColor="var(--text-primary)"
                enabledBgColor="var(--positive-act)"
                disabledBgColor="var(--text-disabled)"
                outLineColor=""
                isLoaderNeeded={true}
                isLoading={isLabelLoading}
                hoverOption="black"
                className="w-width-full"
                isFillNeeded={true}
                inActiveVariantNeeded={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetNewPassword;
